import React, { useState, useEffect, useRef } from 'react';
import {
  getConsent,
  saveConsent,
  acceptAllConsent,
  CONSENT_CATEGORIES,
  CONSENT_DESCRIPTIONS,
} from '../utils/cookieConsent';

/**
 * Consent Preferences Modal Component
 * Allows users to granularly control their cookie consent preferences
 * Fully accessible with keyboard navigation and ARIA attributes
 */
const ConsentPreferencesModal = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState(CONSENT_CATEGORIES);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    // Load current preferences when modal opens
    if (isOpen) {
      const currentConsent = getConsent();
      if (currentConsent) {
        setPreferences(currentConsent);
      } else {
        setPreferences(CONSENT_CATEGORIES);
      }

      // Focus the close button when modal opens
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    // Handle escape key to close modal
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    // Trap focus within modal
    const handleTabKey = (e) => {
      if (!isOpen) return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    // Prevent body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleToggle = (category) => {
    // Necessary cookies cannot be toggled
    if (category === 'necessary') return;

    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAllConsent();
    onClose();
  };

  const handleRejectAll = () => {
    saveConsent({
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      aria-labelledby="consent-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 transition-opacity duration-200"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal container */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col z-10"
        style={{ animation: 'fadeIn 0.2s ease-out' }}
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h2
                  id="consent-modal-title"
                  className="text-xl font-bold text-white"
                >
                  Privacy & Cookie Settings
                </h2>
                <p className="text-sm text-white/80 mt-0.5">
                  Manage your data preferences
                </p>
              </div>
            </div>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-label="Close preferences modal"
            >
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50">
          {/* Cookie categories */}
          <div className="space-y-3">
            {Object.entries(CONSENT_DESCRIPTIONS).map(([category, info]) => {
              const isEnabled = preferences[category];
              const isNecessary = category === 'necessary';

              return (
                <div
                  key={category}
                  className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:shadow transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h3 className="text-sm font-bold text-slate-900">
                          {info.title}
                        </h3>
                        {isNecessary && (
                          <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                            Always Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {info.description}
                      </p>
                    </div>

                    {/* Toggle switch */}
                    <button
                      role="switch"
                      aria-checked={isEnabled}
                      aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${info.title}`}
                      disabled={isNecessary}
                      onClick={() => handleToggle(category)}
                      className={`flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        isEnabled
                          ? 'bg-gradient-to-r from-indigo-600 to-purple-600'
                          : 'bg-slate-300'
                      } ${isNecessary ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          isEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="bg-white border-t border-slate-200 px-6 py-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleRejectAll}
              className="sm:flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
              aria-label="Reject all optional cookies"
            >
              Reject All
            </button>

            <button
              onClick={handleSavePreferences}
              className="sm:flex-1 px-4 py-2.5 text-sm font-semibold text-indigo-600 bg-white border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              aria-label="Save selected preferences"
            >
              Save Preferences
            </button>

            <button
              onClick={handleAcceptAll}
              className="sm:flex-1 px-4 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-sm hover:shadow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              aria-label="Accept all cookies"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentPreferencesModal;
